import React, {useEffect, useState} from "react";
import {DragDropContext} from "react-beautiful-dnd";
import {Button} from "react-bootstrap";
import {
    CORP_TYPE,
    JobDescriptionDetailsType,
    JobDetailType,
    saveJobDescriptionListHelper,
    SourceType,
} from "../jobDetailsManagementHelper";
import {cloneDeep} from "lodash";
import {JobDescriptionSection} from "./JobDescirptionSection";

export const JobDescription: React.FC<{
    jobCorpType: CORP_TYPE;
    selectedJob: JobDetailType;
    jobDescription: JobDescriptionDetailsType[][];
    setJobDescription: any;
    reload: () => Promise<void>;
}> = ({jobCorpType, selectedJob, jobDescription, setJobDescription, reload}) => {
    const {title: jobTitle, id: jobId, salary} = selectedJob;
    const source: SourceType = selectedJob.source || "INTERNAL";
    const [isSavingJobDescription, setSavingJobDescription] = useState<boolean>(false);
    const [savingJobDescriptionMsg, setSavingJobDescriptionMsg] = useState<string>("");

    useEffect(() => {
        setSavingJobDescriptionMsg("");
    }, [jobId]);

    const handleSaveJobDescription = async () => {
        try {
            setSavingJobDescription(true);
            setSavingJobDescriptionMsg("");
            await saveJobDescriptionListHelper(jobCorpType, jobDescription, jobId, source);
            await reload();
            setSavingJobDescriptionMsg("Saved!");
        } catch (e) {
            console.error(e);
            setSavingJobDescriptionMsg("Failed to save. Please try again.");
        } finally {
            setSavingJobDescription(false);
        }
    };

    // handle react-beautiful-dnd
    const updateDescriptionSectionDetails = (
        action: "ADD" | "UPDATE" | "REMOVE",
        sectionIndex: number,
        detailIndex?: number,
        detail?: JobDescriptionDetailsType,
    ) => {
        if (!jobDescription) return;
        if (savingJobDescriptionMsg !== "") setSavingJobDescriptionMsg("");
        switch (action) {
            case "ADD": {
                let updatedJobDescription = cloneDeep(jobDescription);
                updatedJobDescription[sectionIndex].push({} as JobDescriptionDetailsType);
                setJobDescription(updatedJobDescription);
                break;
            }
            case "UPDATE": {
                if (detailIndex !== undefined && detail) {
                    let updatedJobDescription = cloneDeep(jobDescription);
                    updatedJobDescription[sectionIndex][detailIndex] = detail;
                    setJobDescription(updatedJobDescription);
                }
                break;
            }
            case "REMOVE": {
                if (detailIndex !== undefined) {
                    let updatedJobDescription = cloneDeep(jobDescription);
                    updatedJobDescription[sectionIndex].splice(detailIndex, 1);
                    setJobDescription(updatedJobDescription);
                }
                break;
            }
        }
    };

    const updateDescriptionSections = (action: "ADD" | "REMOVE", sectionIndex?: number) => {
        if (!jobDescription) return;
        switch (action) {
            case "ADD": {
                let updatedJobDescription = cloneDeep(jobDescription);
                updatedJobDescription.push([] as JobDescriptionDetailsType[]);
                setJobDescription(updatedJobDescription);
                break;
            }
            case "REMOVE": {
                if (sectionIndex !== undefined) {
                    let updatedJobDescription = cloneDeep(jobDescription);
                    updatedJobDescription.splice(sectionIndex, 1);
                    setJobDescription(updatedJobDescription);
                    break;
                }
                break;
            }
        }
    };

    const onDragEnd = (result: any) => {
        const [sourceSectionIndex, sourceIndex] = result?.draggableId?.split("-");
        const destinationSectionIndex = result?.destination?.droppableId?.split("-")[1];
        const destinationIndex = result?.destination?.index;

        if (
            jobDescription === undefined ||
            sourceSectionIndex === undefined ||
            sourceIndex === undefined ||
            destinationSectionIndex === undefined ||
            destinationIndex === undefined
        )
            return;

        if (sourceSectionIndex === destinationSectionIndex && sourceIndex === destinationIndex) return;

        const updatedJobDescription = cloneDeep(jobDescription);
        const draggedObj = cloneDeep(updatedJobDescription[sourceSectionIndex][sourceIndex]);
        const sourceList = updatedJobDescription[sourceSectionIndex];
        const destinationList = updatedJobDescription[destinationSectionIndex];

        // remove from original position
        sourceList.splice(sourceIndex, 1);

        // add to new position
        destinationList.splice(destinationIndex, 0, draggedObj);

        setJobDescription(updatedJobDescription);
    };

    const handlePreview = () => {
        const key = `JOB-PREVIEW-${jobId}`;
        const previewUrl = `/jobDetailsManagement/preview/${jobId}`;
        sessionStorage.setItem(
            key,
            JSON.stringify({
                sections: jobDescription,
                salary: salary,
                jobTitle: jobTitle,
            }),
        );

        const previewWindow = window.open(previewUrl, key);
        if (previewWindow) {
            previewWindow.close();
            // Reopen after closing
            setTimeout(() => {
                window.open(previewUrl, key);
            }, 200);
        } else {
            window.open(previewUrl, "_blank");
        }
    };

    return (
        <>
            <div>
                <DragDropContext onDragEnd={onDragEnd}>
                    {jobDescription.map((section, index) => {
                        return (
                            <JobDescriptionSection
                                key={index}
                                section={section}
                                sectionIndex={index}
                                salary={salary}
                                updateSections={updateDescriptionSections}
                                updateSectionDetails={updateDescriptionSectionDetails}
                                handlePreview={handlePreview}
                            />
                        );
                    })}
                </DragDropContext>
                <Button
                    className="float-left button-input"
                    size="sm"
                    variant="outline-primary"
                    onClick={() => updateDescriptionSections("ADD")}
                >
                    Add Section
                </Button>
                <br />
                <Button
                    variant="outline-primary"
                    className="float-right"
                    size="sm"
                    onClick={() => handleSaveJobDescription()}
                    disabled={isSavingJobDescription}
                >
                    {isSavingJobDescription ? "Saving..." : "Save your changes"}
                </Button>
                {savingJobDescriptionMsg && <span className="float-right">{savingJobDescriptionMsg}</span>}
            </div>
        </>
    );
};
